import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateFulfillmentMethodsOptions,
  BulkCreateFulfillmentMethodsResponse,
  BulkUpdateFulfillmentMethodTagsApplicationErrors,
  BulkUpdateFulfillmentMethodTagsByFilterApplicationErrors,
  BulkUpdateFulfillmentMethodTagsByFilterOptions,
  BulkUpdateFulfillmentMethodTagsByFilterResponse,
  BulkUpdateFulfillmentMethodTagsOptions,
  BulkUpdateFulfillmentMethodTagsResponse,
  FulfillmentMethod,
  FulfillmentMethodCreatedEnvelope,
  FulfillmentMethodDeletedEnvelope,
  FulfillmentMethodQuery,
  FulfillmentMethodUpdatedEnvelope,
  FulfillmentMethodsQueryBuilder,
  GetAccumulatedFulfillmentMethodsAvailabilityOptions,
  GetAccumulatedFulfillmentMethodsAvailabilityResponse,
  GetAggregatedMethodAvailabilityResponse,
  GetCombinedMethodAvailabilityResponse,
  ListAvailableFulfillmentMethodsForAddressOptions,
  ListAvailableFulfillmentMethodsForAddressResponse,
  ListFulfillmentMethodsOptions,
  ListFulfillmentMethodsResponse,
  QueryFulfillmentMethodsOptions,
  QueryFulfillmentMethodsResponse,
  UpdateFulfillmentMethod,
  bulkCreateFulfillmentMethods as universalBulkCreateFulfillmentMethods,
  bulkUpdateFulfillmentMethodTags as universalBulkUpdateFulfillmentMethodTags,
  bulkUpdateFulfillmentMethodTagsByFilter as universalBulkUpdateFulfillmentMethodTagsByFilter,
  createFulfillmentMethod as universalCreateFulfillmentMethod,
  deleteFulfillmentMethod as universalDeleteFulfillmentMethod,
  getAccumulatedFulfillmentMethodsAvailability as universalGetAccumulatedFulfillmentMethodsAvailability,
  getAggregatedMethodAvailability as universalGetAggregatedMethodAvailability,
  getCombinedMethodAvailability as universalGetCombinedMethodAvailability,
  getFulfillmentMethod as universalGetFulfillmentMethod,
  listAvailableFulfillmentMethodsForAddress as universalListAvailableFulfillmentMethodsForAddress,
  listFulfillmentMethods as universalListFulfillmentMethods,
  queryFulfillmentMethods as universalQueryFulfillmentMethods,
  typedQueryFulfillmentMethods as universalTypedQueryFulfillmentMethods,
  updateFulfillmentMethod as universalUpdateFulfillmentMethod,
} from './restaurants-v1-fulfillment-method-fulfillment-methods.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function createFulfillmentMethod(
  httpClient: HttpClient
): CreateFulfillmentMethodSignature {
  return (
    fulfillmentMethod: NonNullablePaths<
      FulfillmentMethod,
      | `deliveryOptions.deliveryArea`
      | `deliveryOptions.deliveryArea.postalCodeOptions.countryCode`
      | `deliveryOptions.deliveryArea.radiusOptions.centerPointAddress`
      | `deliveryOptions.deliveryArea.radiusOptions.maxDistance`,
      5
    >
  ) =>
    universalCreateFulfillmentMethod(
      fulfillmentMethod,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateFulfillmentMethodSignature {
  /**
   * Creates a new fulfillment method.
   *
   * >**Note:** `fulfillmentMethod.availability.time_zone` uses the time zone specified in the [`language and regions`](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fsettings/language-and-region) settings in the dashboard, regardless of the value provided.
   * @param - Fulfillment method to create.
   * @returns The created fulfillment method.
   */
  (
    fulfillmentMethod: NonNullablePaths<
      FulfillmentMethod,
      | `deliveryOptions.deliveryArea`
      | `deliveryOptions.deliveryArea.postalCodeOptions.countryCode`
      | `deliveryOptions.deliveryArea.radiusOptions.centerPointAddress`
      | `deliveryOptions.deliveryArea.radiusOptions.maxDistance`,
      5
    >
  ): Promise<
    NonNullablePaths<
      FulfillmentMethod,
      | `pickupOptions.address.streetAddress.number`
      | `pickupOptions.address.streetAddress.name`
      | `pickupOptions.address.streetAddress.apt`
      | `pickupOptions.address.subdivisions`
      | `pickupOptions.address.subdivisions.${number}.code`
      | `pickupOptions.address.subdivisions.${number}.name`
      | `deliveryOptions.deliveryArea.radiusOptions.unit`
      | `deliveryOptions.deliveryArea.postalCodeOptions.postalCodes`
      | `deliveryOptions.deliveryArea.customOptions.geocodes`
      | `deliveryOptions.deliveryArea.type`
      | `type`
      | `availability.availableTimes`
      | `availability.availableTimes.${number}.dayOfWeek`
      | `tags.privateTags.tagIds`,
      6
    >
  >;
}

export function bulkCreateFulfillmentMethods(
  httpClient: HttpClient
): BulkCreateFulfillmentMethodsSignature {
  return (options?: BulkCreateFulfillmentMethodsOptions) =>
    universalBulkCreateFulfillmentMethods(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateFulfillmentMethodsSignature {
  /**
   * Create multiple fulfillment methods at once.
   */
  (options?: BulkCreateFulfillmentMethodsOptions): Promise<
    NonNullablePaths<
      BulkCreateFulfillmentMethodsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.fulfillmentMethod.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getFulfillmentMethod(
  httpClient: HttpClient
): GetFulfillmentMethodSignature {
  return (fulfillmentMethodId: string) =>
    universalGetFulfillmentMethod(
      fulfillmentMethodId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetFulfillmentMethodSignature {
  /**
   * Retrieves a fulfillment method.
   * @param - The ID of the fulfillment method to retrieve.
   * @returns The retrieved fulfillment method.
   */
  (fulfillmentMethodId: string): Promise<
    NonNullablePaths<
      FulfillmentMethod,
      | `pickupOptions.address.streetAddress.number`
      | `pickupOptions.address.streetAddress.name`
      | `pickupOptions.address.streetAddress.apt`
      | `pickupOptions.address.subdivisions`
      | `pickupOptions.address.subdivisions.${number}.code`
      | `pickupOptions.address.subdivisions.${number}.name`
      | `deliveryOptions.deliveryArea.radiusOptions.unit`
      | `deliveryOptions.deliveryArea.postalCodeOptions.postalCodes`
      | `deliveryOptions.deliveryArea.customOptions.geocodes`
      | `deliveryOptions.deliveryArea.type`
      | `type`
      | `availability.availableTimes`
      | `availability.availableTimes.${number}.dayOfWeek`
      | `tags.privateTags.tagIds`,
      6
    >
  >;
}

export function updateFulfillmentMethod(
  httpClient: HttpClient
): UpdateFulfillmentMethodSignature {
  return (
    _id: string,
    fulfillmentMethod: NonNullablePaths<UpdateFulfillmentMethod, `revision`, 2>
  ) =>
    universalUpdateFulfillmentMethod(
      _id,
      fulfillmentMethod,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateFulfillmentMethodSignature {
  /**
   * Updates a fulfillment method.
   *
   * Each time the fulfillment method is updated, its revision increments by 1. The existing revision must be included when updating the fulfillment method. This ensures you're working with the latest fulfillment method information, and it prevents unintended overwrites.
   * @param - Fulfillment method ID.
   * @param - Fulfillment method information to update.
   * @returns The updated fulfillment method.
   */
  (
    _id: string,
    fulfillmentMethod: NonNullablePaths<UpdateFulfillmentMethod, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      FulfillmentMethod,
      | `pickupOptions.address.streetAddress.number`
      | `pickupOptions.address.streetAddress.name`
      | `pickupOptions.address.streetAddress.apt`
      | `pickupOptions.address.subdivisions`
      | `pickupOptions.address.subdivisions.${number}.code`
      | `pickupOptions.address.subdivisions.${number}.name`
      | `deliveryOptions.deliveryArea.radiusOptions.unit`
      | `deliveryOptions.deliveryArea.postalCodeOptions.postalCodes`
      | `deliveryOptions.deliveryArea.customOptions.geocodes`
      | `deliveryOptions.deliveryArea.type`
      | `type`
      | `availability.availableTimes`
      | `availability.availableTimes.${number}.dayOfWeek`
      | `tags.privateTags.tagIds`,
      6
    >
  >;
}

export function deleteFulfillmentMethod(
  httpClient: HttpClient
): DeleteFulfillmentMethodSignature {
  return (fulfillmentMethodId: string) =>
    universalDeleteFulfillmentMethod(
      fulfillmentMethodId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteFulfillmentMethodSignature {
  /**
   * Deletes a fulfillment method.
   * @param - The ID of the fulfillment method to delete.
   */
  (fulfillmentMethodId: string): Promise<void>;
}

export function queryFulfillmentMethods(
  httpClient: HttpClient
): QueryFulfillmentMethodsSignature {
  return (options?: QueryFulfillmentMethodsOptions) =>
    universalQueryFulfillmentMethods(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryFulfillmentMethodsSignature {
  /**
   * Creates a query to retrieve a list of fulfillment methods.
   *
   * The `queryFulfillmentMethods()` function builds a query to retrieve a list of fulfillment methods and returns a `FulfillmentMethodsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/fulfillment-methods/fulfillment-methods-query-builder/find) function.
   *
   * You can refine the query by chaining `FulfillmentMethodsQueryBuilder` functions onto the query. `FulfillmentMethodsQueryBuilder` functions enable you to filter, sort, and control the results that `queryFulfillmentMethods()` returns.
   *
   * `queryFulfillmentMethods()` runs with the following `FulfillmentMethodsQueryBuilder` defaults, which you can override:
   *
   * * [`limit(50)`](/fulfillment-methods/fulfillment-methods-query-builder/limit)
   * * [`ascending('entityId')`](/fulfillment-methods/fulfillment-methods-query-builder/ascending)
   *
   * The following `FulfillmentMethodsQueryBuilder` functions are supported for `queryFulfillmentMethods()`. For a full description of the fulfillment method object, see the object returned for the [`items`](/fulfillment-methods/fulfillment-methods-query-result/items) property in `FulfillmentMethodsQueryResult`.
   */
  (options?: QueryFulfillmentMethodsOptions): FulfillmentMethodsQueryBuilder;
}

export function typedQueryFulfillmentMethods(
  httpClient: HttpClient
): TypedQueryFulfillmentMethodsSignature {
  return (
    query: FulfillmentMethodQuery,
    options?: QueryFulfillmentMethodsOptions
  ) =>
    universalTypedQueryFulfillmentMethods(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryFulfillmentMethodsSignature {
  /** */
  (
    query: FulfillmentMethodQuery,
    options?: QueryFulfillmentMethodsOptions
  ): Promise<
    NonNullablePaths<
      QueryFulfillmentMethodsResponse,
      | `fulfillmentMethods`
      | `fulfillmentMethods.${number}.deliveryOptions.deliveryArea.type`
      | `fulfillmentMethods.${number}.type`,
      6
    >
  >;
}

export function listFulfillmentMethods(
  httpClient: HttpClient
): ListFulfillmentMethodsSignature {
  return (options?: ListFulfillmentMethodsOptions) =>
    universalListFulfillmentMethods(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListFulfillmentMethodsSignature {
  /**
   * Retrieves a list of up to 100 fulfillment methods.
   * @param - Options for listing the fulfillment methods.
   */
  (options?: ListFulfillmentMethodsOptions): Promise<
    NonNullablePaths<
      ListFulfillmentMethodsResponse,
      | `fulfillmentMethods`
      | `fulfillmentMethods.${number}.deliveryOptions.deliveryArea.type`
      | `fulfillmentMethods.${number}.type`,
      6
    >
  >;
}

export function listAvailableFulfillmentMethodsForAddress(
  httpClient: HttpClient
): ListAvailableFulfillmentMethodsForAddressSignature {
  return (options?: ListAvailableFulfillmentMethodsForAddressOptions) =>
    universalListAvailableFulfillmentMethodsForAddress(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAvailableFulfillmentMethodsForAddressSignature {
  /**
   * Retrieves a list of up to 100 fulfillment methods available for a given address.
   *
   * The response will only include:
   * - Non-delivery fulfillment methods.
   * - Delivery fulfillment methods that are available to the given address according to their delivery areas.
   * @param - Options for listing the available fulfillment methods.
   */
  (options?: ListAvailableFulfillmentMethodsForAddressOptions): Promise<
    NonNullablePaths<
      ListAvailableFulfillmentMethodsForAddressResponse,
      | `fulfillmentMethods`
      | `fulfillmentMethods.${number}.deliveryOptions.deliveryArea.type`
      | `fulfillmentMethods.${number}.type`,
      6
    >
  >;
}

export function getAccumulatedFulfillmentMethodsAvailability(
  httpClient: HttpClient
): GetAccumulatedFulfillmentMethodsAvailabilitySignature {
  return (options?: GetAccumulatedFulfillmentMethodsAvailabilityOptions) =>
    universalGetAccumulatedFulfillmentMethodsAvailability(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAccumulatedFulfillmentMethodsAvailabilitySignature {
  /**
   * Retrieves the accumulated availability of all fulfillment methods.
   * @deprecated
   */
  (options?: GetAccumulatedFulfillmentMethodsAvailabilityOptions): Promise<
    NonNullablePaths<
      GetAccumulatedFulfillmentMethodsAvailabilityResponse,
      | `availability.availableTimes`
      | `availability.availableTimes.${number}.dayOfWeek`
      | `types`,
      5
    >
  >;
}

export function getCombinedMethodAvailability(
  httpClient: HttpClient
): GetCombinedMethodAvailabilitySignature {
  return (fulfillmentMethodIds: string[]) =>
    universalGetCombinedMethodAvailability(
      fulfillmentMethodIds,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCombinedMethodAvailabilitySignature {
  /**
   * Retrieves the combined availability of a list of fulfillment methods.
   *
   * The combined availability is a list of times during which one or more of the given fulfillment methods is available, and the types of those fulfillment methods.
   * @param - IDs of fulfillment methods used to determine the combined availability.
   * @deprecated
   */
  (fulfillmentMethodIds: string[]): Promise<
    NonNullablePaths<
      GetCombinedMethodAvailabilityResponse,
      | `combinedAvailability.availableTimes`
      | `combinedAvailability.availableTimes.${number}.dayOfWeek`
      | `fulfillmentTypes`,
      5
    >
  >;
}

export function getAggregatedMethodAvailability(
  httpClient: HttpClient
): GetAggregatedMethodAvailabilitySignature {
  return (fulfillmentMethodIds: string[]) =>
    universalGetAggregatedMethodAvailability(
      fulfillmentMethodIds,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAggregatedMethodAvailabilitySignature {
  /**
   * Retrieves the aggregated availability of a list of fulfillment methods.
   *
   * The aggregated availability is a list of times during which one or more of the given fulfillment methods is available, and the types of those fulfillment methods.
   * @param - IDs of fulfillment methods used to determine the aggregated availability.
   */
  (fulfillmentMethodIds: string[]): Promise<
    NonNullablePaths<
      GetAggregatedMethodAvailabilityResponse,
      | `aggregatedAvailability.availableTimes`
      | `aggregatedAvailability.availableTimes.${number}.dayOfWeek`
      | `fulfillmentTypes`,
      5
    >
  >;
}

export function bulkUpdateFulfillmentMethodTags(
  httpClient: HttpClient
): BulkUpdateFulfillmentMethodTagsSignature {
  return (
    fulfillmentMethodIds: string[],
    options?: BulkUpdateFulfillmentMethodTagsOptions
  ) =>
    universalBulkUpdateFulfillmentMethodTags(
      fulfillmentMethodIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateFulfillmentMethodTagsSignature {
  /**
   * Synchronously update tags on multiple fulfillment methods.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - IDs of the fulfillment methods to update tags for.
   */
  (
    fulfillmentMethodIds: string[],
    options?: BulkUpdateFulfillmentMethodTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateFulfillmentMethodTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateFulfillmentMethodTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateFulfillmentMethodTagsByFilter(
  httpClient: HttpClient
): BulkUpdateFulfillmentMethodTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateFulfillmentMethodTagsByFilterOptions
  ) =>
    universalBulkUpdateFulfillmentMethodTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateFulfillmentMethodTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple fulfillment methods according to the specified filter.
   * If a filter isn't specified, this method updates all fulfillment methods.
   * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
   * @param - Filter that determines which fulfillment methods to update tags for.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateFulfillmentMethodTagsByFilterOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateFulfillmentMethodTagsByFilterResponse,
      `jobId`,
      2
    > & {
      __applicationErrorsType?: BulkUpdateFulfillmentMethodTagsByFilterApplicationErrors;
    }
  >;
}

export const onFulfillmentMethodCreated = EventDefinition(
  'wix.restaurants.v1.fulfillment_method_created',
  true,
  (event: FulfillmentMethodCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.pickupOptions.address' },
            {
              path: 'entity.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress',
            },
            { path: 'entity.dineInOptions.address' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
            },
            {
              path: 'entity.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
            },
          ],
        },
      ])
    )
)<FulfillmentMethodCreatedEnvelope>();
export const onFulfillmentMethodDeleted = EventDefinition(
  'wix.restaurants.v1.fulfillment_method_deleted',
  true,
  (event: FulfillmentMethodDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'undefined.pickupOptions.address' },
            {
              path: 'undefined.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress',
            },
            { path: 'undefined.dineInOptions.address' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'undefined.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
            },
            {
              path: 'undefined.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
            },
          ],
        },
      ])
    )
)<FulfillmentMethodDeletedEnvelope>();
export const onFulfillmentMethodUpdated = EventDefinition(
  'wix.restaurants.v1.fulfillment_method_updated',
  true,
  (event: FulfillmentMethodUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.pickupOptions.address' },
            {
              path: 'entity.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress',
            },
            { path: 'entity.dineInOptions.address' },
            { path: 'modifiedFields.pickupOptions.address' },
            {
              path: 'modifiedFields.deliveryOptions.deliveryArea.radiusOptions.centerPointAddress',
            },
            { path: 'modifiedFields.dineInOptions.address' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
            },
            {
              path: 'entity.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
            },
            {
              path: 'modifiedFields.deliveryOptions.deliveryArea.customOptions.geocodes.latitude',
            },
            {
              path: 'modifiedFields.deliveryOptions.deliveryArea.customOptions.geocodes.longitude',
            },
          ],
        },
      ])
    )
)<FulfillmentMethodUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AdditionalInfoLabel,
  Address,
  AddressHint,
  AddressLocation,
  App,
  ApplicationError,
  Availability,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateFulfillmentMethodResult,
  BulkCreateFulfillmentMethodsOptions,
  BulkCreateFulfillmentMethodsRequest,
  BulkCreateFulfillmentMethodsResponse,
  BulkUpdateFulfillmentMethodTagsByFilterOptions,
  BulkUpdateFulfillmentMethodTagsByFilterRequest,
  BulkUpdateFulfillmentMethodTagsByFilterResponse,
  BulkUpdateFulfillmentMethodTagsOptions,
  BulkUpdateFulfillmentMethodTagsRequest,
  BulkUpdateFulfillmentMethodTagsResponse,
  BulkUpdateFulfillmentMethodTagsResult,
  BusinessSchedule,
  Categories,
  ChangeContext,
  ChangeContextPayloadOneOf,
  CommonAddress,
  CommonAddressStreetOneOf,
  CommonBulkActionMetadata,
  CommonItemMetadata,
  ConsentPolicy,
  CreateFulfillmentMethodRequest,
  CreateFulfillmentMethodResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomArea,
  CustomConfig,
  CustomTag,
  DayOfWeek,
  DayOfWeekAvailability,
  DeleteFulfillmentMethodRequest,
  DeleteFulfillmentMethodResponse,
  DeliveryArea,
  DeliveryAreaAreaOptionsOneOf,
  DeliveryInfo,
  DineInInfo,
  DineInInfoAdditionalInfoLabelOptionsOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntitiesDayOfWeek,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  File,
  FulfillmentMethod,
  FulfillmentMethodCreatedEnvelope,
  FulfillmentMethodDeletedEnvelope,
  FulfillmentMethodMethodOptionsOneOf,
  FulfillmentMethodQuerySpec,
  FulfillmentMethodType,
  FulfillmentMethodUpdatedEnvelope,
  FulfillmentMethodsQueryBuilder,
  FulfillmentMethodsQueryResult,
  GeoCoordinates,
  GetAccumulatedFulfillmentMethodsAvailabilityOptions,
  GetAccumulatedFulfillmentMethodsAvailabilityRequest,
  GetAccumulatedFulfillmentMethodsAvailabilityResponse,
  GetAggregatedMethodAvailabilityRequest,
  GetAggregatedMethodAvailabilityResponse,
  GetCombinedMethodAvailabilityRequest,
  GetCombinedMethodAvailabilityResponse,
  GetFulfillmentMethodRequest,
  GetFulfillmentMethodResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  ListActiveFulfillmentMethodsRequest,
  ListActiveFulfillmentMethodsResponse,
  ListAvailableFulfillmentMethodsForAddressOptions,
  ListAvailableFulfillmentMethodsForAddressRequest,
  ListAvailableFulfillmentMethodsForAddressResponse,
  ListFulfillmentMethodsOptions,
  ListFulfillmentMethodsRequest,
  ListFulfillmentMethodsResponse,
  Locale,
  MessageEnvelope,
  Multilingual,
  Page,
  Pages,
  PickupInfo,
  PlacementType,
  PostalCode,
  Properties,
  PropertiesChange,
  QueryFulfillmentMethodsOptions,
  QueryFulfillmentMethodsRequest,
  QueryFulfillmentMethodsResponse,
  Radius,
  ResolutionMethod,
  RestoreInfo,
  SiteCloned,
  SiteCreated,
  SitePropertiesEvent,
  SitePropertiesNotification,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  SupportedLanguage,
  TagList,
  Tags,
  TimeOfDay,
  TimeOfDayRange,
  TimePeriod,
  Translation,
  Type,
  URI,
  URIs,
  Unit,
  UpdateFulfillmentMethod,
  UpdateFulfillmentMethodRequest,
  UpdateFulfillmentMethodResponse,
  WebhookIdentityType,
  utils,
} from './restaurants-v1-fulfillment-method-fulfillment-methods.universal.js';
